import { IdealCustomerProfile } from '../types';

export interface WorkflowExample {
  id: string;
  title: string;
  subtitle: string;
  steps: string[];
  outcome: string;
  query: string;
  type: 'companies' | 'people';
  icon: 'linkedin' | 'airbnb' | 'building' | 'github' | 'salesforce';
  accentColor: string;
}

export function generateWorkflowExamples(icp: IdealCustomerProfile | null): WorkflowExample[] {
  const industry = icp?.solution_products?.toLowerCase().includes('saas')
    ? 'B2B SaaS'
    : icp?.solution_products?.toLowerCase().includes('fintech')
    ? 'fintech'
    : icp?.solution_products?.toLowerCase().includes('health')
    ? 'healthtech'
    : 'B2B SaaS';

  const primaryTitle = icp?.titles?.split(',')[0]?.trim() || 'VP of Sales';
  const primaryTitlePlural = primaryTitle.endsWith('s') ? primaryTitle : `${primaryTitle}s`;
  const companySize = icp?.company_sizes?.[0] || '50-200 employees';
  const region = icp?.target_region?.[0] || 'United States';
  const fundingStage = icp?.funding_stages?.[0] || 'Series A';

  return [
    {
      id: '1',
      title: 'Funded Companies Pipeline',
      subtitle: `${industry} companies with recent funding`,
      steps: [
        `Find ${industry} companies, ${companySize}, in ${region} that raised ${fundingStage}`,
        `Enrich with ${primaryTitle} name, verified work email, and LinkedIn`,
        'Start personalized outreach sequence',
      ],
      outcome: 'Pipeline from funding news in hours',
      query: `Find ${industry} companies with ${companySize} in ${region} that raised ${fundingStage} in the last 90 days`,
      type: 'companies',
      icon: 'building',
      accentColor: '#059669',
    },
    {
      id: '2',
      title: 'LinkedIn Post Commenters',
      subtitle: 'Engaged prospects from viral AI posts',
      steps: [
        `Find ${primaryTitlePlural} at ${industry} companies (${companySize}) posting on LinkedIn`,
        'Enrich with verified work email, title, and company revenue',
        'Send helpful outreach referencing their post',
      ],
      outcome: '~200 warm leads showing buying intent',
      query: `Find ${primaryTitlePlural} at ${industry} companies with ${companySize} posting about growth challenges`,
      type: 'people',
      icon: 'linkedin',
      accentColor: '#0A66C2',
    },
    {
      id: '3',
      title: 'New Airbnb Superhosts',
      subtitle: 'Hosts who just earned Superhost status',
      steps: [
        'Find Airbnb hosts in Austin who earned Superhost status this quarter with 3+ listings',
        'Enrich with owner name, email, total bookings, and estimated annual revenue',
        'Send congratulatory outreach with property management offer',
      ],
      outcome: '~80 motivated property entrepreneurs',
      query: 'Find Airbnb hosts in Austin, TX who just earned Superhost status this quarter and manage 3 or more active listings.',
      type: 'people',
      icon: 'airbnb',
      accentColor: '#FF5A5F',
    },
    {
      id: '4',
      title: 'LangChain Contributors',
      subtitle: 'AI framework developers at enterprises',
      steps: [
        'Find LangChain and CrewAI contributors who work at Fortune 500 companies',
        'Enrich with employer, role, GitHub activity, and Twitter/X handle',
        'Send developer partnership or hiring outreach',
      ],
      outcome: '~120 influential AI engineers',
      query: 'Find contributors to LangChain, CrewAI, and AutoGen repositories on GitHub who currently work at Fortune 500 or well-funded startups.',
      type: 'people',
      icon: 'github',
      accentColor: '#171515',
    },
    {
      id: '5',
      title: 'Dreamforce Speakers',
      subtitle: 'Salesforce ecosystem decision makers',
      steps: [
        `Find people who spoke at Dreamforce, Director+ at companies using Salesforce`,
        'Enrich with verified email, company size, tech stack, and talk topic',
        'Send personalized outreach referencing their session',
      ],
      outcome: '~150 proven thought leaders',
      query: `Find people who spoke at Dreamforce. Focus on Director, VP, or C-level titles at companies with 200+ employees.`,
      type: 'people',
      icon: 'salesforce',
      accentColor: '#00A1E0',
    },
  ];
}
